import { Backbone } from "js/vendor";
import Adapter from "./adapter";
import IndexedDbAdapter from "./indexedDbAdapter";
import FirebaseAdapter from "./firebaseAdapter";
import isConnected from "../utilities/isConnected";
import log from "../logger";

const _events = Object.assign({}, Backbone.Events);

/**
 * The ConnectionToggleAdapter will connect to an onlineAdapter if the data is not stored for offline use.
 */
class ConnectionToggleAdapter extends Adapter {
    constructor(options) {
        super(options);
        this.onlineAdapter = options.onlineAdapter || new FirebaseAdapter(Object.assign({}, options, { autoSync: false }));
        this.offlineAdapter = new IndexedDbAdapter(options);
        this.currentAdapter = this.offlineAdapter;
    }

    isConnected() {
        return this.currentAdapter.isConnected();
    }

    connect(options) {
        this.options = Object.assign({}, options);

        if (this.id) {
            _events.off("reload:" + this.id);
        }
        this.id = this._connectAdapter(this.offlineAdapter, { onRemoteError: this._handleRemoteError.bind(this) });

        _events.on("reload:" + this.id, () => {
            this._connectAdapter(this.offlineAdapter, { onRemoteError: this._handleRemoteError.bind(this) });
        });

        return this.id;
    }

    _handleRemoteError(err) {
        if (err && err.statusCode === 404 && isConnected.connected) {
            this._connectAdapter(this.onlineAdapter);
        } else if (this.options.onRemoteError) {
            this.options.onRemoteError(err);
        }
    }

    _connectAdapter(adapter, options = {}) {
        if (!adapter.isConnected()) {
            try {
                const id = adapter.connect(Object.assign({}, this.options, options));
                this.currentAdapter = adapter;
                return id;
            } catch (e) {
                log.warn(e);
                return null;
            }
        } else {
            adapter.refresh();
            return this.id;
        }
    }

    disconnect() {
        _events.off("reload:" + this.id);
        this.onlineAdapter.disconnect();
        this.offlineAdapter.disconnect();
    }

    update(type, newData, currentData) {
        return this.currentAdapter.update(type, newData, currentData);
    }

    createUid(root) {
        return this.currentAdapter.createUid(root);
    }
}

/**
 * A way to trigger a reload.
 * @param id
 */
ConnectionToggleAdapter.reload = function(id) {
    // Firebase ids are unique across collections, so we'll keep this simple and trigger on the id
    _events.trigger("reload:" + id);
};

export default ConnectionToggleAdapter;
